import { TransportLocationService } from 'src/app/angularJS/services/transport-location/transport-location.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';
import { LinkBuilder } from 'src/app/angularJS/services/link-builder.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { UserMessagesService } from 'src/app/angularJS/services/user-messages.service';
import { registerLocaleData } from '@angular/common';

export class TransportLocationsUploadController {
  public static $inject = [
    'transportLocationService',
    'transportLocationsResponse',
    '$state',
    'userService',
    'LinkBuilder',
    '$rootScope',
    'breadcrumbService',
    'ACTToolbarService',
    'QueryBuilder',
    'UserMessages'
  ];
  public uploadDetail: any = {};
  public transportLocations: Array<InterACT.Interfaces.TransportLocations.TransportLocation> = [];
  public paginatorData: InterACT.Interfaces.IPaginationData = {
    perPageCount: 15,
    currentPage: 1,
    totalCount: 0
  };
  public searchText: string = undefined;

  constructor(
    private transportLocationService: TransportLocationService,
    transportLocationsResponse: InterACT.Interfaces.TransportLocations.TransportLocationsResponse,
    private $state: ng.ui.IStateService,
    private userService: UserService,
    private linkBuilder: LinkBuilder,
    private $rootScope: InterACT.Interfaces.IActoraRootScope,
    private breadcrumbService: BreadcrumbService,
    private toolbarService: ACTToolbarService,
    private queryBuilder: QueryBuilder,
    private userMessages: UserMessagesService
  ) {
    this.activate();
    this.transportLocations = transportLocationsResponse.transportLocations;
    this.paginatorData.totalCount = transportLocationsResponse.meta.totalCount;
  }

  /**
   * A method that gets a page worth of transport locations
   * @param pageNumber the number of the page to retrieve
   * @param perPageCount the amount of transport locations per page to retrieve
   */
  public getTransportLocations = (pageNumber: number, perPageCount: number) => {
    if (!perPageCount) {
      perPageCount = this.paginatorData.perPageCount;
    }

    this.$rootScope.showSpinner = true;

    const query = this.searchText === undefined ? this.queryBuilder
      .newParam()
      .fieldIs('scheme.link', this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id))
      .sortBy([{ columnName: 'locationName', direction: 'asc' }])
      .getParam()
    : this.queryBuilder
      .newParam()
      .fieldIs('scheme.link', this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id))
      .and()
      .openSubQuery()
      .fieldContains('locationName', this.searchText)
      .or()
      .fieldContains('references.atcoCode', this.searchText)
      .or()
      .fieldContains('references.nlcCode', this.searchText)
      .closeSubQuery()
      .sortBy([{ columnName: 'locationName', direction: 'asc' }])
      .getParam();

    this.transportLocationService.getList(pageNumber, perPageCount, query).then((response) => {
      this.paginatorData.totalCount = response.meta.totalCount;
      this.transportLocations = response.transportLocations;
      this.$rootScope.showSpinner = false;
    });
  }

  /**
   * A method that spawns a confirm dialog asking the user if they wish to delete the supplied location.
   * The location is deleted if the user confirms.
   */
  public deleteTransportLocation = (transportLocation: InterACT.Interfaces.TransportLocations.TransportLocation) => {
    this.userMessages.buildConfirmDialog(
      'DELETE_TRANSPORT_LOCATION',
      'CONFIRM_DIALOG.TRANSPORT_LOCATIONS.DELETE_CONFIRM',
      undefined,
      'CONFIRM_DIALOG.TRANSPORT_LOCATIONS.CONFIRM',
      'CONFIRM_DIALOG.TRANSPORT_LOCATIONS.CANCEL'
    ).then(() => {
      this.$rootScope.showSpinner = true;
      this.transportLocationService.delete(transportLocation).then((response) => {
        this.userMessages.showToast('TOAST_MESSAGE.TRANSPORT_LOCATIONS.DELETED');
        setTimeout(() => {
          this.getTransportLocations(1, 15);
        }, 1000);
      });
    });
  }

  /**
   * A method that gets invoked when a file has been imported into the browser
   */
  public onFileImported = () => {
    const r = new FileReader();
    r.onload = (e: any) => {
      const contents = e.target.result;
      const transportLocations = this.createTransportLocationsFromString(contents);

      if (transportLocations.length > 0) {
        this.$state.go('transport-locations-publisher', { transportLocations: transportLocations });
      }
    };

    r.readAsText(this.uploadDetail.file, 'ISO-8859-1');
  };

  /**
 * A method that returns the reference data for the supplied transport location
 * @param transportLocation the location to get the reference data for
 */
  public getLocationReferenceData(transportLocation: InterACT.Interfaces.TransportLocations.TransportLocation) {
    const locationReferenceData = {
      name: undefined,
      value: undefined
    };

    if (!transportLocation.references) {
      return locationReferenceData;
    }

    for (const referenceTypeName in transportLocation.references) {
      locationReferenceData.name = referenceTypeName;
      locationReferenceData.value = transportLocation.references[referenceTypeName];
      return locationReferenceData;
    }
  }

  /**
   * A method that gets called during controller instantiation
   */
  private activate() {
    this.toolbarService.getToolbar('primary').then((toolbar) => {
      toolbar.setTitle('TRANSPORT_LOCATIONS');
    });

    this.breadcrumbService.setBreadcrumbs([{
      name: 'TRANSPORT_LOCATIONS',
      sref: 'transport-locations-upload'
    }, {
      name: 'LOCATION_UPLOADS',
      sref: 'transport-locations-upload'
    }]);
  }

  /**
 * Creates transport locations from a csv string
 * @param transportLocationStringData a string that contains transport location data
 */
  private createTransportLocationsFromString(transportLocationStringData: string) {
    const transportLocations: Array<InterACT.Interfaces.TransportLocations.TransportLocation> = [];

    const scheme = {
      abbreviation: this.userService.selectedScheme.reference,
      link: this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id)
    };

    const transportLocationsDataStrings = transportLocationStringData.split('\r\n');

    if (transportLocationsDataStrings.length < 2) {
      return transportLocations;
    }

    for (let c = 1; c < transportLocationsDataStrings.length; c++) {
      const transportLocationString = transportLocationsDataStrings[c];
      const locationDataParts = transportLocationString.split(',');

      if (locationDataParts.length < 3) {
        continue;
      }

      const references = {};
      references[locationDataParts[1]] = locationDataParts[2];

      const transportLocation = {
        scheme: scheme,
        locationName: locationDataParts[0],
        references: references,
        status: 'NOT_UPLOADED',
        failureMessage: undefined
      };

      transportLocations.push(<any>transportLocation);
    }

    return transportLocations;
  }

  /**
   * Searches and displays the existing transport locations on the scheme for matches within various fields
   * @param searchQuery the search query written by the user 
   */

  public search = (searchQuery: string) => {
    if ( '' === searchQuery ) {
      this.searchText = undefined;
      this.getTransportLocations(1, this.paginatorData.perPageCount);
      return;
    }

    var transportLocationQuery = this.queryBuilder
      .newParam()
      .fieldIs('scheme.link', this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id))
      .and()
      .openSubQuery()
      .fieldContains('locationName', searchQuery)
      .or()
      .fieldContains('references.atcoCode', searchQuery)
      .or()
      .fieldContains('references.nlcCode', searchQuery)
      .closeSubQuery()
      .sortBy([{ columnName: 'locationName', direction: 'asc' }])
      .getParam();

    this.$rootScope.showSpinner = true;
    this.paginatorData.currentPage = 1;
    this.transportLocationService.getList(this.paginatorData.currentPage, this.paginatorData.perPageCount, transportLocationQuery).then((response) => {
      this.transportLocations = response.transportLocations.length > 0 ? response.transportLocations : [];
      this.paginatorData.totalCount = response.meta.totalCount;
      this.$rootScope.showSpinner = false;
    });
    
    this.searchText = searchQuery;
  }
}